import React from 'react';
import { Dropdown, Spin } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import StatusTag from './StatusTag';
import type { StatusTagType } from './StatusTag';

interface StatusSelectProps {
    /** StatusTag type (usually "event"). */
    type: StatusTagType;
    /** Current status value. */
    value: string;
    /** Ordered list of selectable status values. */
    options: string[];
    onChange: (value: string) => void;
    loading?: boolean;
    disabled?: boolean;
}

/**
 * Inline, editable status cell for tables: a StatusTag + chevron trigger that
 * opens a dropdown of statuses. Left-aligns cleanly with the column header
 * (no marginLeft hacks) and keeps the tag + chevron vertically centered.
 */
const StatusSelect: React.FC<StatusSelectProps> = ({ type, value, options, onChange, loading, disabled }) => (
    <Dropdown
        trigger={['click']}
        disabled={disabled || loading}
        menu={{
            selectable: true,
            selectedKeys: [value],
            items: options.map(v => ({ key: v, label: <StatusTag type={type} value={v} className="m-0" /> })),
            onClick: ({ key }) => {
                if (key !== value) onChange(key);
            },
        }}
    >
        <button
            type="button"
            disabled={disabled || loading}
            onClick={e => e.stopPropagation()}
            className="flex w-full items-center justify-between gap-2 cursor-pointer border-0 bg-transparent p-0 outline-none disabled:cursor-not-allowed"
        >
            <StatusTag type={type} value={value} className="m-0" />
            {loading ? (
                <Spin size="small" />
            ) : (
                <DownOutlined className="text-[10px] text-muted-foreground flex-shrink-0" />
            )}
        </button>
    </Dropdown>
);

export default StatusSelect;
